import { useEffect, useRef, useState, useCallback, memo, useMemo } from 'react'
import DOMPurify from 'dompurify'
import _ from 'lodash'
import { isMobile } from 'react-device-detect'
import TagManager from 'react-gtm-module'
import { useLocation, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { IssuableCouponType } from '../../@types/coupon'
import { ProductDetailType, ProductDetailBaseInfo, ProductDutyInfo, ProductDetailCategory2 } from '../../@types/product'
import { getIssuableCoupon } from '../../api/coupons'
import { getProductDetail, postRecentProducts } from '../../api/product'
import AppBar from '../../components/AppBar'
import ProductDetailCarousel from '../../components/product/ProductDetailCarousel'
import ProductMobileActions from '../../components/product/ProductMobileActions'
import ProductPriceInfoMobile from '../../components/product/ProductPriceInfoMobile'
import ProductPriceInfoPC from '../../components/product/ProductPriceInfoPC'
import ProductModal from '../../components/product/ProductModal'
import ProductPay from '../../components/product/ProductPay'
import ProductDetailReviews from '../../components/product/ProductDetailReviews'
import ProductTabButtons from '../../components/product/ProductTabButtons'
import ProductDetailContents from '../../components/product/ProductDetailContents'
import { useAppContext } from '../../components/provider/AppProvider'
import { useCommonContext } from '../../components/provider/CommonProvider'
import '../../css/Product.css'
import { sendSetAppsflyerEvent } from '../../js/postMessage'
import {
  formatNumberWithTruncation,
  getParsedSessionStorageItem,
  plusComma,
  extractValuesFromArrayOfObjects,
  extractKeysFromArrayOfObjects,
} from '../../js/script'
import { GTMItems } from '../../@types/dataLayerObject'
import SEO from '../../SEO'
import useDateCheck from '../../hooks/useDateCheck'

interface AnyObject {
  [key: string]: any
}

const ProductDetail = () => {
  // 더보기 버튼 클릭시 상세 리뷰 내용 노출
  const [productItem, setProductItem] = useState<ProductDetailBaseInfo>()
  const [product, setProduct] = useState<ProductDetailType>()
  const [wish, setWish] = useState<boolean>(false)
  const [isModal, setIsModal] = useState<boolean>(false)
  const [couponSort, setCouponSort] = useState<IssuableCouponType[]>([])
  const [couponDown, setCouponDown] = useState<boolean>(false)
  const [couponCheck, setCouponCheck] = useState<boolean>(false)
  const [disCountAcc, setDisCountAcc] = useState<number>(0)
  const [checkDown, setCheckDown] = useState<boolean>(false)
  const [productInfo, setProductInfo] = useState<ProductDutyInfo>()
  const [keyArray, setKeyArray] = useState<string[]>([])
  const [valueArray, setValueArray] = useState<string[]>([])
  const [closeCheck, setCloseCheck] = useState<boolean>(false)
  const [osCheck, setOsCheck] = useState<string>('')
  const [prevItems, setPrevItems] = useState<GTMItems[]>([])
  const productPayRef = useRef<HTMLDivElement>(null)
  const section1Ref = useRef(null)
  const section2Ref = useRef(null)
  const { isAppView } = useAppContext()
  const { hasAccessToken } = useCommonContext()
  const param = useParams()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)

  const items =
    isAppView && queryParams.get('items') ? JSON.parse(queryParams.get('items') || '') : location.state?.items
  const checkDate = useDateCheck('2025-01-20', 'range', '2025-02-01')

  const starCountArray: number[] = useMemo(() => [0, 0, 0, 0, 0], [])
  const sections = useMemo(
    () => [
      { name: 'detail', ref: section1Ref, label: '상세정보' },
      { name: 'review', ref: section2Ref, label: '리뷰', count: product?.counter?.reviewCnt },
    ],
    [product]
  )

  // 할인 금액을 계산하는 함수
  const calculateDiscount = useCallback((salePrice: number, immediateDiscountAmt: number): number => {
    const discount = (salePrice - immediateDiscountAmt) * 0.1
    return Number(formatNumberWithTruncation(discount).replaceAll(',', ''))
  }, [])

  // useEffect 내부 로직을 함수로 분리
  const updateArrayWithDiscount = useCallback(
    (array: Array<AnyObject>, product: ProductDetailType) => {
      if (array && array.length > 0) {
        const discount = calculateDiscount(product.price?.salePrice, product.price?.immediateDiscountAmt)

        array[0].reserve_discount = discount
        array[0].pay_back = product.price?.accumulationAmtWhenBuyConfirm
      }
    },
    [calculateDiscount]
  )

  useEffect(() => {
    if (!items || !product || prevItems.length > 0) return

    updateArrayWithDiscount(items, product)
    setPrevItems(items)
  }, [items, product, updateArrayWithDiscount])

  useEffect(() => {
    if (!window.ReactNativeWebView) return
    const path = location.pathname + location.search
    sendSetAppsflyerEvent({
      eventName: 'screenEvent',
      eventType: {
        name: 'About_fishing',
        class: path,
      },
    })
  }, [location.pathname, location.search])

  //gtm
  // NOTE: 공통된 아이템 객체를 생성하는 함수
  const createItemObject = useCallback(
    (product: ProductDetailType, productItem: ProductDetailBaseInfo, categoryArray: ProductDetailCategory2[]) => ({
      item_id: productItem?.productNo,
      item_name: productItem?.productName,
      affiliation: '어바웃피싱',
      item_list_price: product.price?.salePrice,
      item_selling_price: product.price?.salePrice - product.price?.immediateDiscountAmt,
      price: product.price?.salePrice - product.price?.immediateDiscountAmt,
      item_total_discount: product.price?.immediateDiscountAmt,
      discount: 0,
      index: undefined,
      item_brand: product.brand?.name,
      item_category: categoryArray[0]?.categories ? categoryArray[0]?.categories[0]?.label : null,
      item_category2: categoryArray[0]?.categories ? categoryArray[0]?.categories[1]?.label : null,
      item_category3: categoryArray[0]?.categories ? categoryArray[0]?.categories[2]?.label : null,
      item_variant: undefined,
      item_list_id: categoryArray[0]?.categories[0]?.categoryNo,
      item_list_name: categoryArray[0]?.categories[0]?.label,
    }),
    []
  )

  const createEventObject = useCallback(
    (
      product: ProductDetailType,
      productItem: ProductDetailBaseInfo,
      categoryArray: ProductDetailCategory2[],
      price: number,
      items: GTMItems[] = []
    ) => {
      const itemObjects = items.length > 0 ? items : [createItemObject(product, productItem, categoryArray)]
      return {
        currency: 'KRW',
        value: price,
        reserve_discount: 0,
        pay_back: product.price?.accumulationAmtWhenBuyConfirm,
        items: itemObjects,
      }
    },
    [createItemObject]
  )

  const createViewItem = useCallback(
    (
      product: ProductDetailType,
      productItem: ProductDetailBaseInfo,
      categoryArray: ProductDetailCategory2[],
      price: number,
      items: GTMItems[] = []
    ) => ({
      dataLayer: {
        event: 'view_item',
        ecommerce: createEventObject(product, productItem, categoryArray, price, items),
      },
    }),
    [createEventObject]
  )

  const createAppsflyerEvent = useCallback(
    (
      product: ProductDetailType,
      productItem: ProductDetailBaseInfo,
      categoryArray: ProductDetailCategory2[],
      price: number,
      items: GTMItems[] = []
    ) => ({
      eventName: 'view_item',
      eventType: createEventObject(product, productItem, categoryArray, price, items),
    }),
    [createEventObject]
  )

  const handleEvent = useCallback(
    (
      product: ProductDetailType,
      productItem: ProductDetailBaseInfo,
      categoryArray: ProductDetailCategory2[],
      price: number,
      items: GTMItems[],
      isAppView: boolean
    ) => {
      const viewItem = createViewItem(product, productItem, categoryArray, price, items)
      if (isAppView) {
        const appsflyerEvent = createAppsflyerEvent(product, productItem, categoryArray, price, items)
        sendSetAppsflyerEvent(appsflyerEvent)
      } else {
        TagManager.dataLayer(viewItem)
      }
    },
    [createViewItem, createAppsflyerEvent]
  )

  const getCategoryArray = useCallback((product: ProductDetailType) => {
    const filterCategory = product.categories.filter(
      (item: ProductDetailCategory2) => item.categories[0].label !== '브랜드'
    )
    return [filterCategory[0]]
  }, [])

  const calculatePrice = useCallback((product: ProductDetailType, prevItems: GTMItems[]) => {
    if (!prevItems?.length) {
      const salePrice = product.price?.salePrice || 0
      const discountAmt = product.price?.immediateDiscountAmt || 0
      return salePrice - discountAmt
    } else {
      return prevItems.reduce((total: number, el: GTMItems) => {
        const itemPrice = el.price ?? 0 // el.price가 undefined인 경우 0으로 설정
        return total + itemPrice
      }, 0)
    }
  }, [])

  // NOTE: GTM/AppsFlyer 이벤트 실행
  useEffect(() => {
    if (!product || !productItem || Object.keys(product).length === 0) return
    const categoryArray = getCategoryArray(product)
    const price = calculatePrice(product, prevItems)
    const items = prevItems?.length ? prevItems : []
    handleEvent(product, productItem, categoryArray, price, items, isAppView)
  }, [product])

  useEffect(() => {
    const os = getParsedSessionStorageItem('os')
    setOsCheck(os)
  }, [location])

  // 하단 푸터
  useEffect(() => {
    const footer = document.querySelector('.footer_wrap') as HTMLElement
    footer.style.paddingBottom = isMobile ? '108px' : '0px'
  }, [location.pathname])

  //상품 상세
  useEffect(() => {
    if (!param.id) return
    getProductDetail(param.id)
      .then((res) => {
        setProductItem(res.data.baseInfo)
        setProduct(res.data)
        setWish(res.data.liked)
      })
      .catch((err) => console.error(err))
  }, [param.id])

  // 상품 쿠폰 가져오기
  useEffect(() => {
    if (!param.id) return

    getIssuableCoupon(param.id)
      .then((res) => {
        const sortCoupon = res.data.filter((el: any) => el.downloadable)
        setCouponSort(sortCoupon)
      })
      .catch((err) => console.error(err))
  }, [param.id, checkDown, couponCheck])

  //최근 본 상품에 넣기
  const updateRecentProducts = useCallback((id: number) => {
    const productNo = getParsedSessionStorageItem('mallProductNo') || []
    const array = _.isArray(productNo) ? productNo : []

    if (_.includes(array, id)) return

    array.push(id)
    window.sessionStorage.setItem('mallProductNo', JSON.stringify(array))
  }, [])

  useEffect(() => {
    if (param.id === undefined) return
    const productId = Number(param.id)
    if (hasAccessToken) {
      postRecentProducts(productId)
        .then()
        .catch((err) => console.error(err))
    } else {
      updateRecentProducts(productId)
    }
  }, [param.id])

  useEffect(() => {
    if (productItem !== undefined) {
      const data = productItem?.dutyInfo
      setProductInfo(JSON.parse(data))
    }
  }, [productItem])

  useEffect(() => {
    if (productInfo) {
      const keys = extractKeysFromArrayOfObjects(productInfo.contents)
      setKeyArray(keys as string[])
      const values = extractValuesFromArrayOfObjects(productInfo.contents)
      setValueArray(values as string[])
    }
  }, [productInfo])

  //적립금 1000단위에서 짜르기
  useEffect(() => {
    if (product) {
      const count = formatNumberWithTruncation((product.price?.salePrice - product.price?.immediateDiscountAmt) * 0.1)
      setDisCountAcc(Number(count.replace(/,/g, '')))
    }
  }, [product])

  if (!product) return null

  return (
    <>
      {location.pathname.includes('product') && productItem && (
        <SEO title={productItem?.productName} image={`https:${productItem?.imageUrls[0]}`} />
      )}
      {/* mobile 시작 */}
      {isMobile ? (
        <div className={isAppView ? 'main_wrap' : closeCheck ? 'main_wrap' : 'main_wrap product'}>
          <div className='contents_wrap'>
            {/* 상세페이지 상단 AppBar */}
            <AppBar setCloseCheck={setCloseCheck} closeCheck={closeCheck} />
            {/* 상품 상세 wrap */}
            <div className='product_detail_wrap'>
              {/* 이미지 swipe 영역 */}
              <div className='product_detail_image_slide' style={{ minHeight: '350px' }}>
                <ProductDetailCarousel imageUrls={productItem?.imageUrls} isMobile />
              </div>
              {/* 상품 정보 */}
              <div className='product_info'>
                <ProductPriceInfoMobile
                  product={product}
                  productItem={productItem}
                  starCountArray={starCountArray}
                  couponSort={couponSort}
                  couponDown={couponDown}
                  setCouponDown={setCouponDown}
                  setCouponCheck={setCouponCheck}
                  setCheckDown={setCheckDown}
                  disCountAcc={disCountAcc}
                  osCheck={osCheck}
                />
              </div>
              {/* NOTE: 상세 정보 - 현재 사용 안함 */}
              {/* {productItem?.contentHeader && (
                <>
                  <ProductDetailInfoSection
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(productItem?.contentHeader),
                    }}
                  ></ProductDetailInfoSection>
                </>
              )} */}
              {/* 상품 상세 정보, 리뷰 탭 : mobile */}
              <ProductTabButtons sections={sections} />

              {/* 주문 전 유의사항 */}
              <ProductDetailNotice ref={section1Ref} className='product_detail_notice'>
                <p className='title'>주문 전 유의사항</p>
                <p>
                  결제 완료 후 또는 입고 확정 이후에도 품절/결품이 발생될 수 있으며, 재고 확인으로 인한 발송 지연도 있을
                  수 있으며, 고객님께 문자서비스를 통해 품절/결품 안내를 드리고 있습니다.
                </p>
              </ProductDetailNotice>
              {productItem?.accumulationUseYn === 'N' && (
                <img
                  src={'https://company.aboutfishing.kr/shop/images/payment_guide.png'}
                  alt='적립금 사용불가 상품 안내'
                  style={{ width: '100%' }}
                />
              )}
              {/* {checkDate && (
                <img
                  src={'/images/product_detail/product-detail-shpping-lunar-new-year2.jpg'}
                  alt='배송지연 안내'
                  style={{ width: '100%', marginBottom: '32px' }}
                />
              )} */}
              {/* 상세 이미지 영역, 상품 고시 정보, 배송/환불/안내, 판매자 목록 영역 아코디언 */}
              <ProductDetailContents
                product={product}
                productItem={productItem}
                productInfo={productInfo}
                keyArray={keyArray}
                valueArray={valueArray}
              />

              {/* 상품 리뷰 영역 & 상품 Q&A 영역 */}
              <ProductDetailReviews
                sectionRef={section2Ref}
                product={product}
                productItem={productItem}
                starCountArray={starCountArray}
              />
            </div>
          </div>
        </div>
      ) : (
        // pc 시작
        <div className='main_wrap'>
          <div className='contents_wrap'>
            {/* 상품 상세 wrap */}
            <div className='product_detail_wrap'>
              <div className='product_left_info'>
                <div className='swier_cont' style={{ minHeight: '540px' }}>
                  {/* 이미지 swipe 영역 */}
                  <ProductDetailCarousel imageUrls={productItem?.imageUrls} isMobile={false} />
                </div>

                {/* 상품 상세 정보, 리뷰 탭 */}
                <ProductTabButtons sections={sections} />

                {/* 주문 전 유의사항 */}
                <ProductDetailNotice ref={section1Ref} className='product_detail_notice'>
                  <p className='title'>주문 전 유의사항</p>
                  <p>
                    결제 완료 후 또는 입고 확정 이후에도 품절/결품이 발생될 수 있으며, 재고 확인으로 인한 발송 지연도
                    있을 수 있으며, 고객님께 문자서비스를 통해 품절/결품 안내를 드리고 있습니다.
                  </p>
                </ProductDetailNotice>
                {productItem?.accumulationUseYn === 'N' && (
                  <img
                    src={'https://company.aboutfishing.kr/shop/images/payment_guide.png'}
                    alt='적립금 사용불가 상품 안내'
                    style={{ width: '100%' }}
                  />
                )}
                {/* {checkDate && (
                  <img
                    src={'/images/product_detail/product-detail-shpping-lunar-new-year2.jpg'}
                    alt='배송지연 안내'
                    style={{ width: '100%', marginBottom: '32px' }}
                  />
                )} */}
                {/* 상세 이미지 영역, 상품 고시 정보, 배송/환불/안내, 판매자 목록 영역 아코디언 */}
                <ProductDetailContents
                  product={product}
                  productItem={productItem}
                  productInfo={productInfo}
                  keyArray={keyArray}
                  valueArray={valueArray}
                />
                <hr />

                {/* 상품 리뷰 영역 & 상품 Q&A 영역 */}
                <ProductDetailReviews
                  sectionRef={section2Ref}
                  product={product}
                  productItem={productItem}
                  starCountArray={starCountArray}
                />
              </div>
              <div className='product_right_info'>
                {/* 상품 정보 */}
                <ProductPriceInfoPC
                  product={product}
                  productItem={productItem}
                  prevItems={prevItems}
                  couponSort={couponSort}
                  couponDown={couponDown}
                  setCouponDown={setCouponDown}
                  setCouponCheck={setCouponCheck}
                  disCountAcc={disCountAcc}
                  wish={wish}
                  setWish={setWish}
                  osCheck={osCheck}
                />
                {/* 상세 정보 */}
                {productItem?.contentHeader && (
                  <div className='product_detail_info'>
                    <div className='info_title'>배송비</div>
                    <div className='info_contents mb24'>{plusComma(product?.deliveryFee?.deliveryAmt)}원</div>

                    <div className='info_title'>상품정보</div>
                    <div
                      className='info_contents'
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(productItem?.contentHeader),
                      }}
                    ></div>
                  </div>
                )}

                {/* PC 장바구니 담기, 구매하기 */}
                <ProductPay
                  productPayRef={productPayRef}
                  selectProductNo={param.id}
                  product={product}
                  prevItems={prevItems}
                  listItem={product}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 찜하기, 장바구니, 구매하기 (fixed) */}
      {isMobile && (
        <ProductMobileActions
          product={product}
          productItem={productItem}
          prevItems={prevItems}
          wish={wish}
          setWish={setWish}
          setIsModal={setIsModal}
        />
      )}

      {isModal && product && productInfo && productItem && (
        <ProductModal
          setIsModal={setIsModal}
          selectProductNo={param.id}
          headerText='옵션선택'
          product={product}
          listItem={product}
          prevItems={prevItems}
        />
      )}
    </>
  )
}

export default memo(ProductDetail)

// 주문 전 유의사항
export const ProductDetailNotice = styled.div`
  padding: 32px 20px;
  & p {
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    color: #898989;
  }
  & p.title {
    font-size: 15px;
    font-weight: 600;
    line-height: 16px;
    margin-bottom: 8px;
    color: var(--colBlack);
  }
  @media screen and (min-width: 768px) {
    padding: 32px 0;
    & p {
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      color: #898989;
    }
    & p.title {
      font-size: 16px;
      font-weight: 600;
      line-height: 20px;
      margin-bottom: 10px;
      color: var(--colBlack);
    }
  }
`
